import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import styled from "styled-components";

import { useAdminScope } from "../../context/AdminScopeContext";
import { AdminButton } from "../../pages/admin/action_engine";
import { usePanelActions } from "../../pages/admin/panel_engine/consumer";
import {
  AdminEmptyState,
  AdminErrorState,
  AdminLoadingState,
} from "../../pages/admin/ui_engine";
import {
  DEFAULT_INVENTORY_PAGE_SIZE,
  getInventoryErrorMessage,
  listInventory,
} from "../../services/inventoryAdminApi";

import InventoryList from "./inventory/InventoryList";
import {
  normalizeInventoryResponse,
} from "./inventory/inventoryPresentation";


import {
  AdminFilterSelect,
  AdminFormToolbar,
  AdminPagination,
  AdminSearchInput,
} from "../../pages/admin/section_engine";

export const INVENTORY_ADMIN_PANEL_STATUS =
  Object.freeze({
    LOADING: "loading",
    EMPTY: "empty",
    ERROR: "error",
    READY: "ready",
  });


const EMPTY_PAGINATION = Object.freeze({
  page: 1,
  page_size: DEFAULT_INVENTORY_PAGE_SIZE,
  total: 0,
  total_pages: 0,
  has_previous: false,
  has_next: false,
});


export default function SupplyChainInventoryAdminPanel() {
  const {
    selectedCompanyUuid,
    selectedActivityUuid,
  } = useAdminScope();

  const [status, setStatus] = useState(
    INVENTORY_ADMIN_PANEL_STATUS.LOADING,
  );
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(
    EMPTY_PAGINATION,
  );
  const [
    selectedItem,
    setSelectedItem,
  ] = useState(null);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sort, setSort] = useState("product");
  const [direction, setDirection] = useState("asc");
  const [errorMessage, setErrorMessage] = useState("");
  const [refreshing, setRefreshing] = useState(false);


  const loadInventory = useCallback(
    async ({
      requestedPage = 1,
      requestedSearch = search,
      requestedStatus = statusFilter,
      requestedSort = sort,
      requestedDirection = direction,
      preserveData = false,
    } = {}) => {
      if (preserveData) {
        setRefreshing(true);
      } else {
        setStatus(
          INVENTORY_ADMIN_PANEL_STATUS.LOADING,
        );
      }

      setErrorMessage("");

      try {
        const payload = await listInventory({
          companyUuid: selectedCompanyUuid,
          activityUuid: selectedActivityUuid,
          page: requestedPage,
          pageSize: DEFAULT_INVENTORY_PAGE_SIZE,
          search: requestedSearch,
          status: requestedStatus,
          sort: requestedSort,
          direction: requestedDirection,
        });

        const normalized =
          normalizeInventoryResponse(
            payload,
            DEFAULT_INVENTORY_PAGE_SIZE,
          );

        setItems(normalized.items);
        setPagination(normalized.pagination);

        setSelectedItem((current) => {
          if (!normalized.items.length) {
            return null;
          }

          const preserved =
            normalized.items.find(
              (item) => (
                item.product_uuid
                === current?.product_uuid
              ),
            );

          return preserved || normalized.items[0];
        });

        setStatus(
          normalized.items.length
            ? INVENTORY_ADMIN_PANEL_STATUS.READY
            : INVENTORY_ADMIN_PANEL_STATUS.EMPTY,
        );
      } catch (error) {
        if (!preserveData) {
          setItems([]);
          setPagination(EMPTY_PAGINATION);
          setSelectedItem(null);
          setStatus(
            INVENTORY_ADMIN_PANEL_STATUS.ERROR,
          );
        }

        setErrorMessage(
          getInventoryErrorMessage(
            error,
            "Impossible de charger l’état du stock.",
          ),
        );
      } finally {
        setRefreshing(false);
      }
    },
    [
      direction,
      search,
      selectedCompanyUuid,
      selectedActivityUuid,
      sort,
      statusFilter,
    ],
  );


  useEffect(() => {
    loadInventory({
      requestedPage: 1,
      requestedSearch: search,
      requestedStatus: statusFilter,
      requestedSort: sort,
      requestedDirection: direction,
    });
  }, [
    direction,
    loadInventory,
    search,
    sort,
    statusFilter,
  ]);


  const refresh = useCallback(() => (
    loadInventory({
      requestedPage: pagination.page,
      requestedSearch: search,
      requestedStatus: statusFilter,
      requestedSort: sort,
      requestedDirection: direction,
      preserveData: items.length > 0,
    })
  ), [
    direction,
    items.length,
    loadInventory,
    pagination.page,
    search,
    sort,
    statusFilter,
  ]);


  usePanelActions(
    useMemo(
      () => [
        {
          key: "refresh-inventory",
          label: refreshing
            ? "Actualisation en cours"
            : "Actualiser le stock",
          icon: "↻",
          onClick: refresh,
          disabled:
            refreshing
            || status
              === INVENTORY_ADMIN_PANEL_STATUS.LOADING,
        },
      ],
      [
        refresh,
        refreshing,
        status,
      ],
    ),
  );


  const handleSearchSubmit = (event) => {
    event.preventDefault();

    const normalized = searchInput.trim();

    if (normalized === search) {
      loadInventory({
        requestedPage: 1,
        requestedSearch: normalized,
        requestedStatus: statusFilter,
        requestedSort: sort,
        requestedDirection: direction,
      });
      return;
    }

    setSearch(normalized);
  };


  const handleResetSearch = () => {
    setSearchInput("");

    if (!search) {
      loadInventory({
        requestedPage: 1,
        requestedSearch: "",
        requestedStatus: statusFilter,
        requestedSort: sort,
        requestedDirection: direction,
      });
      return;
    }

    setSearch("");
  };


  return (
    <PanelRoot>
      <AdminFormToolbar onSubmit={handleSearchSubmit}>
        <AdminSearchInput
          type="search"
          value={searchInput}
          onChange={(event) => {
            setSearchInput(event.target.value);
          }}
          placeholder="Rechercher un produit"
          aria-label="Rechercher un produit"
        />

        <AdminFilterSelect
          aria-label="Filtrer par statut de stock"
          value={statusFilter}
          onChange={(event) => {
            setStatusFilter(event.target.value);
          }}
        >
          <option value="">Tous les statuts</option>
          <option value="in_stock">En stock</option>
          <option value="low_stock">
            Risque de rupture
          </option>
          <option value="out_of_stock">
            Rupture
          </option>
        </AdminFilterSelect>

        <AdminFilterSelect
          aria-label="Trier l’inventaire"
          value={sort}
          onChange={(event) => {
            setSort(event.target.value);
          }}
        >
          <option value="product">Produit</option>
          <option value="stock">Stock physique</option>
          <option value="available">
            Stock disponible
          </option>
          <option value="coverage">Couverture</option>
          <option value="demand">
            Vitesse de vente
          </option>
          <option value="snapshot">
            Date du relevé
          </option>
        </AdminFilterSelect>

        <AdminFilterSelect
          aria-label="Sens du tri"
          value={direction}
          onChange={(event) => {
            setDirection(event.target.value);
          }}
        >
          <option value="asc">Croissant</option>
          <option value="desc">Décroissant</option>
        </AdminFilterSelect>

        <AdminButton
          variant="primary"
          type="submit"
        >
          Rechercher
        </AdminButton>

        {(search || searchInput) && (
          <AdminButton
            variant="secondary"
            type="button"
            onClick={handleResetSearch}
          >
            Réinitialiser
          </AdminButton>
        )}
      </AdminFormToolbar>


      {status
        === INVENTORY_ADMIN_PANEL_STATUS.LOADING && (
        <AdminLoadingState
          message="Chargement de l’état du stock..."
        />
      )}


      {status
        === INVENTORY_ADMIN_PANEL_STATUS.ERROR && (
        <AdminErrorState
          message={errorMessage}
          compact
        />
      )}


      {status
        === INVENTORY_ADMIN_PANEL_STATUS.EMPTY && (
        <AdminEmptyState
          message={
            search || statusFilter
              ? "Aucun produit ne correspond aux filtres sélectionnés."
              : "Aucune donnée de stock n’est disponible."
          }
        />
      )}


      {status
        === INVENTORY_ADMIN_PANEL_STATUS.READY && (
        <>
          <Summary>
            <strong>{pagination.total}</strong>{" "}
            produit
            {pagination.total > 1 ? "s" : ""}
            {refreshing
              ? " — actualisation…"
              : ""}
          </Summary>

          <InventoryList
            items={items}
            selectedProductUuid={
              selectedItem?.product_uuid || ""
            }
            onSelectItem={setSelectedItem}
          />

          <AdminPagination
            previousDisabled={
              !pagination.has_previous
              || refreshing
            }
            nextDisabled={
              !pagination.has_next
              || refreshing
            }
            onPrevious={() => {
              loadInventory({
                requestedPage: Math.max(
                  1,
                  pagination.page - 1,
                ),
              });
            }}
            onNext={() => {
              loadInventory({
                requestedPage:
                  pagination.page + 1,
              });
            }}
            label={`Page ${pagination.page} / ${Math.max(
              pagination.total_pages,
              1,
            )}`}
          />

          {selectedItem && (
            <SelectionSummary>
              <span>Produit sélectionné</span>
              <strong>
                {selectedItem.product_name
                  || selectedItem.product_reference
                  || selectedItem.product_uuid}
              </strong>
            </SelectionSummary>
          )}
        </>
      )}
    </PanelRoot>
  );
}


const PanelRoot = styled.section`
  min-width: 0;
  color: #f7f7f7;
`;

const Summary = styled.p`
  margin: 0 0 0.75rem;
  color: rgba(247, 247, 247, 0.68);
`;

const SelectionSummary = styled.div`
  display: grid;
  gap: 0.25rem;
  margin-top: 1rem;
  padding: 0.85rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);

  span {
    color: rgba(247, 247, 247, 0.58);
    font-size: 0.8rem;
  }
`;
